import { FC, useRef, useMemo, useState, useEffect } from 'react'
import { Paper, Box, CircularProgress, styled } from '@mui/material'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
} from '@/components/table/StyledTableComponents'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import type { UserSortableTableProps } from '@/utilities/types/table-props'
import { useUserVirtualizedTable } from '@/hooks'
import { UserTableFooter } from '../../UserTableFooter'
import { VirtualCell } from '../../StyledTable'
import { useNavigate } from 'react-router-dom'
import { useInboxVirtualizedTable } from '@/hooks/shared/useInboxVirtualizedTable'
import { readNotification } from '@/api'
import useAxios from '@/utilities/useAxios'

const DND_SERVICE = `${import.meta.env.VITE_DND_APP_BACKEND_URL}`

const LoadingContainer = styled(Box)({
  p: 4,
  textAlign: 'center',
  height: 'calc(90vh - 60px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

export const UnreadDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.error.main,
  flexShrink: 0,
  marginRight: theme.spacing(1),
}))
interface TableProps extends UserSortableTableProps<any> {}

export const InboxManagementTable: FC<TableProps> = ({
  data = [],
  isLoading,
  filterState,
  error,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  mutateInboxes,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)

  const navigate = useNavigate()

  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useInboxVirtualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  const handleCloseNoResults = () => setShowNoResults(false)

  const handleClick = async (ticketNo: string, id: number) => {
    await readNotification(id)
    mutateInboxes()

    const role = JSON.parse(localStorage.getItem('role') || '')
    const userId = JSON.parse(localStorage.getItem('userId') || '')

    const url = `${DND_SERVICE}/v2/ticket-view/search?page=0&size=10&keyword=${encodeURIComponent(ticketNo)}&roleName=${encodeURIComponent(role)}&userID=${encodeURIComponent(userId)}&isProcessed=false`

    let isProcessed = false

    try {
      const { data } = await useAxios.get(url)
      const content = data?.data?.content ?? []

      if (Array.isArray(content) && content.length === 0) {
        isProcessed = true
      }
    } catch (err) {
      console.error('Error fetching ticket data:', err)
    }

    navigate(
      `/maintenance-tickets?ticketNo=${ticketNo}&isProcessed=${isProcessed}`,
    )
  }

  const renderVirtualRows = useMemo(() => {
    return virtualRows.map((virtualRow) => {
      const row = paginatedItems[virtualRow.index]
      if (!row) return null

      return (
        <VirtualRow
          key={`${row.id}-${page}`}
          sx={{
            transform: `translateY(${virtualRow.start}px)`,
            gap: 2,
          }}
        >
          <VirtualCell
            key={row.id}
            width="100%"
            onClick={() => handleClick(row.ticketNo, row.id)}
            sx={{
              cursor: 'pointer',
              px: 2,
              borderBottom: '1px solid #eee',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                width: '100%',
                overflow: 'hidden',
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  maxWidth: { xs: '100%', sm: '70%' },
                  fontSize: '0.95rem',
                }}
              >
                <UnreadDot
                  sx={{ backgroundColor: row.isRead ? '#c2c2c2' : undefined }}
                />
                {row.message}
              </Box>
              <Box
                sx={{
                  fontSize: '0.75rem',
                  color: 'gray',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  textAlign: { xs: 'left', sm: 'right' },
                  my: { xs: 0.5, sm: 0 },
                }}
              >
                {new Date(row.dateCreated).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Box>
            </Box>
          </VirtualCell>
        </VirtualRow>
      )
    })
  }, [virtualRows, paginatedItems, page])

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <TableWrapper>
      <NoResultsAlert open={showNoResults} onClose={handleCloseNoResults} />
      <Paper
        elevation={2}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <TableContainerStyled>
          <VirtualTableContainer
            ref={parentRef}
            onScroll={handleVirtualizedScroll}
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}
          >
            <div
              style={{
                height: `${totalSize}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {renderVirtualRows}
            </div>
          </VirtualTableContainer>
          <UserTableFooter
            rowsPerPage={rowsPerPage}
            totalCount={totalElem}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainerStyled>
      </Paper>
    </TableWrapper>
  )
}
